import React, { FunctionComponent, useRef } from 'react';
import Post from '../../components/Post';
import MarkdownPost from '../../components/MarkdownPost';
import MainLayout from '../../layouts'
import Grid from '../../components/Grid';
import Breadcrumbs from '../../components/Breadcrumbs';
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import { fetchEntries } from "../../contentful";
import { ParsedUrlQuery } from "querystring";
import { Article } from '../../types/common/Article.types';
import { Comment } from '../../types/common/Comment.type';
import { formatDateAndTimeWithSeparator, formatDate } from '../../utils/formatDate';
import CommentsList from '../../components/CommentsList';
import PostAuthor from '../../components/PostAuthor';
import PageNewsletter from '../../components/Newsletter/PageNewsletter';
import CaptchaProvider from '../../providers/CaptchaProvider';
import { mapLocale } from '../../lib/locales';
import BuyCoffee from '../../components/BuyCoffee';
import NotTranslated from '../../components/NotTranslated';
import RecommendedContent from '../../components/RecommendedContent';
import { useTranslations } from '../../hooks/useTranslations';
import styles from '../../styles/Article.module.scss';
import { generateRandomNumber } from '../../lib/random';
import type { MarkdownArticle } from '../../lib/articles';
import type { Author } from '../../types/common/Author.types';
import type { ContentType } from '../../components/RecommendedContent';

const MAX_RELATED_CONTENT_ITEMS = 3;

const BlogPost: FunctionComponent<{ body: Article | MarkdownArticle, comments: Comment[], markdownAuthor?: Author }> = ({body, comments, markdownAuthor}) => {
  const isMarkdownArticle = body && 'source' in body && body.source === 'markdown';
  const { head, author, content, title, sources, summary, excerpt, featuredImage, createdDate, categoryName, level, isTranslationReady, related } = body as Article;
  const commentsRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslations();
  const articleRenderKey = isMarkdownArticle
    ? `${body.id}:${(body as MarkdownArticle).locale}:${(body as MarkdownArticle).isTranslationReady}`
    : `${body.id}:${title}`;

  return body ? (
    <CaptchaProvider>
      <MainLayout head={isMarkdownArticle ? (body as MarkdownArticle).head || {} as any : head ? head.fields : {}}>
        <Grid>
          <Breadcrumbs />
          {!isTranslationReady && <NotTranslated />} 
          <React.Fragment key={articleRenderKey}>
            {isMarkdownArticle ? (
              <MarkdownPost
                article={body as MarkdownArticle}
                author={markdownAuthor}
                numberOfComments={comments.length}
                commentsBlockRef={commentsRef}
                createdDate={formatDate({
                  dateObject: (body as MarkdownArticle).publicationDate,
                  formatString: 'dd MMMM yyyy',
                  locale: (body as MarkdownArticle).locale,
                })}
              />
            ) : (
              <Post
                content={content}
                title={title}
                sources={sources}
                author={author}
                summary={summary}
                excerpt={excerpt}
                featuredImage={featuredImage}
                numberOfComments={comments.length}
                commentsBlockRef={commentsRef}
                createdDate={createdDate}
                categoryName={categoryName}
                level={level}
              />
            )}
          </React.Fragment>
          {isMarkdownArticle ? markdownAuthor && <PostAuthor author={markdownAuthor}/> : author[0] && <PostAuthor author={author[0].fields}/>}
          <div ref={commentsRef}>
            <CommentsList comments={comments} postId={body.id} title={title} />
          </div>
          {related?.length > 0 && (
            <div className={styles.relatedContentSection}>
              <RecommendedContent
                content={related}
                title={t.ARTICLE.RELATED.TITLE}
                text={t.ARTICLE.RELATED.TEXT}  
              />
            </div>
          )}
          <PageNewsletter />
        </Grid>
      </MainLayout>
      <BuyCoffee />
    </CaptchaProvider>
  ) : null;
};

const getComments = async (articleId: string) => {
  if (!articleId) {
    return [];
  }

  const commentsRes = await fetchEntries({
    content_type: 'comment',
    include: 2,
    'fields.article.sys.id': articleId,
  });

  return commentsRes.data
    .map(({ fields: { message, email, author }, sys: { createdAt }}) => ({ 
      message,
      email: email || '',
      author,
      createdDate: formatDateAndTimeWithSeparator({
        dateObject: createdAt,
        dateFormatString: 'dd MMMM yyyy',
        timeFormatString: 'HH:mm',
        separator: 'o'
      })
    }));
};

const getMarkdownAuthor = async (article: MarkdownArticle, locale: string) => {
  const { getLocalArticleAuthor, getLocalAuthor } = await import('../../lib/authors');
  const articleAuthor = article.author?.[0];
  const localAuthor = getLocalArticleAuthor({
    authors: article.author,
    locale,
  });

  if (localAuthor) {
    return localAuthor;
  }

  if (articleAuthor) {
    try {
      const authorRes = await fetchEntries({
        content_type: 'author',
        include: 2,
        limit: 1,
        'fields.name': articleAuthor,
        locale: mapLocale(locale),
      });

      if (authorRes.data[0]?.fields) {
        return authorRes.data[0].fields;
      }
    } catch (e) {}
  }

  return getLocalAuthor({
    author: 'mateusz-jablonski',
    locale,
  });
};

const getRelationSlug = (relation) => typeof relation === 'string'
  ? relation
  : relation.slug;

const getRelationType = (relation) => typeof relation === 'string'
  ? 'article'
  : relation.type;

const mapRelatedContentfulEntry = (entry, locale) => ({
  type: entry.sys.contentType.sys.id,
  ...entry.fields,
  createdDate: formatDate({
    dateObject: entry.fields.createdDate,
    formatString: 'dd MMMM yyyy',
    locale,
  }),
});

const fetchContentfulRelatedBySlugs = async ({ relations, locale }) => {
  const groupedRelations = relations.reduce((groups, relation) => {
    const type = getRelationType(relation);
    const slug = getRelationSlug(relation);

    if (!slug) {
      return groups;
    }

    return {
      ...groups,
      [type]: [
        ...(groups[type] || []),
        slug,
      ],
    };
  }, {});

  const relatedEntries = await Promise.all(
    Object.entries(groupedRelations).map(async ([contentType, slugs]) => {
      const res = await fetchEntries({
        content_type: contentType,
        include: 2,
        'fields.slug[in]': (slugs as string[]).toString(),
        locale: mapLocale(locale),
      });

      return res.data.map((entry) => mapRelatedContentfulEntry(entry, locale));
    })
  );

  return relatedEntries
    .flat()
    .sort((a, b) => {
      const aIndex = relations.findIndex((relation) => getRelationType(relation) === a.type && getRelationSlug(relation) === a.slug);
      const bIndex = relations.findIndex((relation) => getRelationType(relation) === b.type && getRelationSlug(relation) === b.slug);

      return aIndex - bIndex;
    });
};

const getMarkdownRelatedContent = async ({
  article,
  locale,
}: {
  article: MarkdownArticle;
  locale: string;
}): Promise<ContentType[]> => {
  const { getMarkdownArticle, getRelatedMarkdownArticles } = await import('../../lib/articles');
  const explicitRelations = article.related || [];
  const missingLocalArticleRelations = explicitRelations
    .filter((relation) => getRelationType(relation) === 'article')
    .filter((relation) => !getMarkdownArticle({ slug: getRelationSlug(relation), locale }));
  const contentfulRelations = explicitRelations
    .filter((relation) => getRelationType(relation) !== 'article')
    .concat(missingLocalArticleRelations);
  const explicitContentfulRelated = contentfulRelations.length
    ? await fetchContentfulRelatedBySlugs({ relations: contentfulRelations, locale })
    : [];
  const localRelated = getRelatedMarkdownArticles({
    article,
    locale,
    limit: MAX_RELATED_CONTENT_ITEMS,
  });
  const relatedByKey = new Map<string, ContentType>();

  [
    ...explicitContentfulRelated,
    ...localRelated,
  ].forEach((relatedItem) => {
    relatedByKey.set(`${relatedItem.type}:${relatedItem.slug}`, relatedItem);
  });

  return explicitRelations
    .map((relation) => relatedByKey.get(`${getRelationType(relation)}:${getRelationSlug(relation)}`))
    .filter(Boolean)
    .concat(localRelated.filter((relatedItem) => !explicitRelations.some((relation) => (
      getRelationType(relation) === relatedItem.type && getRelationSlug(relation) === relatedItem.slug
    ))))
    .slice(0, MAX_RELATED_CONTENT_ITEMS) as ContentType[];
};

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext<ParsedUrlQuery>) => {
  const {slug} = context.params
  const locale = context.locale || 'pl';
  const { getMarkdownArticle, isMarkdownArticlePublished } = await import('../../lib/articles');
  const markdownArticle = getMarkdownArticle({
    slug: slug as string,
    locale,
  });
  const fallbackMarkdownArticle = !markdownArticle && locale !== 'pl'
    ? getMarkdownArticle({
      slug: slug as string,
      locale: 'pl',
    })
    : null;
  const selectedMarkdownArticle = markdownArticle || fallbackMarkdownArticle;

  if (selectedMarkdownArticle) {
    if (process.env.NODE_ENV !== 'development' && !isMarkdownArticlePublished(selectedMarkdownArticle)) {
      return {
        notFound: true,
      };
    }

    const { highlightMarkdownCodeBlocks } = await import('../../lib/codeHighlighting');

    return {
      props: {
        body: {
          ...selectedMarkdownArticle,
          id: selectedMarkdownArticle.legacyContentfulId || selectedMarkdownArticle.id,
          isTranslationReady: fallbackMarkdownArticle ? false : selectedMarkdownArticle.isTranslationReady,
          highlightedCodeBlocks: await highlightMarkdownCodeBlocks(selectedMarkdownArticle.content),
          related: await getMarkdownRelatedContent({
            article: selectedMarkdownArticle,
            locale,
          }),
        },
        comments: await getComments(selectedMarkdownArticle.legacyContentfulId),
        markdownAuthor: await getMarkdownAuthor(selectedMarkdownArticle, locale),
      }
    };
  }

  const res = await fetchEntries({
    content_type: 'article',
    'fields.slug': slug,
    include: 2,
    locale: mapLocale(context.locale),
  });

  const articleEntry = res?.data?.[0];

  if (!articleEntry) {
    return {
      notFound: true,
    };
  }

  const tagsObjects = articleEntry.metadata?.tags || [];
  const tags = tagsObjects.map(tag => tag.sys.id);

  const relatedIdsAddedByUser = (articleEntry.fields.related || []).map(r => r.sys.id);
  const idsToExcludeFromRelated = [...relatedIdsAddedByUser, articleEntry.sys.id];

  const body = await res.data
    .map(p => ({ 
      ...p.fields,
      id: p.sys.id,
      related: (p.fields.related || []).map(r => ({
        type: r.sys.contentType.sys.id,
        ...r.fields,
        createdDate: formatDate({
          dateObject: r.fields.createdDate,
          formatString: 'dd MMMM yyyy',
          locale: context.locale,
        }),
      })),
      createdDate: formatDate({
        dateObject: p.fields.createdDate,
        formatString: 'dd MMMM yyyy',
        locale: context.locale,
      })
    }))
    .shift();

  const missingItemsForRelated = MAX_RELATED_CONTENT_ITEMS - body.related.length;

  const relatedRes = tags.length
    ? await fetchEntries({
      include: 2,
      'sys.id[nin]': idsToExcludeFromRelated.toString(),
      'metadata.tags.sys.id[in]': tags.toString(),
    })
    : { data: [] };

  const relatedToShow = [];
  let n = 0;

  if (relatedRes.data.length <= missingItemsForRelated) {
    relatedToShow.push(...Array.from({length: relatedRes.data.length},(_, k) => k));
  } else {
    while (n < Math.min(missingItemsForRelated, relatedRes.data.length)) {
      const randomId = generateRandomNumber(0, relatedRes.data.length - 1);
      if (!relatedToShow.includes(randomId)) {
        relatedToShow.push(randomId);
        n++;
      }
    }
  }

  const related = await relatedRes.data
    .filter((_, i) => relatedToShow.includes(i))
    .map(p => ({
      type: p.sys.contentType.sys.id,
      ...p.fields,
      createdDate: formatDate({
        dateObject: p.fields.createdDate,
        formatString: 'dd MMMM yyyy',
        locale: context.locale,
      }),
    }));

  const comments = await getComments(body.id);

  return {
    props: {
      body: {
        ...body,
        related: [
          ...body.related,
          ...related,
        ]
      },
      comments,
    }
  };
}

export default BlogPost;
